import React, { useRef, useEffect, useState, useCallback, JSX} from 'react';
import ReactDOM from 'react-dom';
import { ChevronUp, ChevronDown, PictureInPicture2, SquareArrowOutDownLeft} from 'lucide-react';
import { Button } from '@/components/ui/button';
import GestureDetector from './ai/GestureDetector';
import BlurDetection from './ai/BlurDetector';
import SpeechDetector from './ai/SpeechDetector';
import FaceDetectors from './ai/FaceDetectors';
import FaceRecognitionOverlay from './ai/FaceRecognitionOverlay';
// import FaceRecognitionIntegrated from '../ai-components/FaceRecognitionIntegrated';
import useCameraProcessor from './ai/useCameraProcessor';
import { AnomalyType } from '@/types/reportanomaly.types';
import { useAuthStore } from '@/store/auth-store';
import { useCourseStore } from '@/store/course-store';
import type { FloatingVideoProps } from '@/types/video.types';
import { useReportAnomalyAudio, useReportAnomalyImage } from '@/hooks/hooks';
import {registerStream, unRegisterStream, getStream} from "@/lib/MediaRegistry";
import { runProctoringChecks } from "@/utils/proctoring/proctoringGuard";
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { FaceRegistrationModal } from './ai/FaceRegistrationModal';

// let flag = 0;
function FloatingVideo({
  isVisible,
  setDoGesture,
  settings,
  rewindVid,
  setRewindVid,
  pauseVid,
  setPauseVid,
  setAnomalies,
  readyToDetect,
  setReadyToDetect,
  anomalies = []
}: FloatingVideoProps): JSX.Element | null {
  useEffect(() => {
    return () => {
       unRegisterStream("floating-video-restart-stream")
       unRegisterStream("floating-video-audio-stream")
    };
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraCheckIntervalRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [size, setSize] = useState({ width: 224, height: 196 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [overlayPosition, setOverlayPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('top-right');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const [anomaly, setAnomaly] = useState(false);
  const [anomalyType, setAnomalyType] = useState("");
   const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [isVideoActive, setIsVideoActive] = useState(false);
  
  // Grace period state for anomaly detection
  const [anomalyDetectionStartTime, setAnomalyDetectionStartTime] = useState<number | null>(null);
  const gracePeriod = 10000; // 10 seconds grace period

  // Original aspect ratio (maintain the initial component ratio)
  const ORIGINAL_ASPECT_RATIO = 320 / 280; // width / height from initial size

  // AI Detection States
  const [isBlur, setIsBlur] = useState("No");
  const [isSpeaking, setIsSpeaking] = useState("No");
  const [gesture, setGesture] = useState("No Gesture Detected ❌");
  const [isFocused, setIsFocused] = useState(true); 
  const [facesCount, setFacesCount] = useState(0);
  const [recognizedFaces, setRecognizedFaces] = useState<any[]>([]);
  const [hasFaceRecognitionMismatch, setHasFaceRecognitionMismatch] = useState(false);
  const [showFaceRegisterModal, setShowFaceRegisterModal] = useState(false);
  const [penaltyPoints, setPenaltyPoints] = useState(0);
  const [penaltyType, setPenaltyType] = useState("");
  const [contiguousAnomalyPoints, setContiguousAnomalyPoints] = useState(0);

  // Thumbs-up challenge states
  const [isThumbsUpChallenge, setIsThumbsUpChallenge] = useState(false);
  const [thumbsUpCountdown, setThumbsUpCountdown] = useState(0);
  const [lastChallengeTime, setLastChallengeTime] = useState(Date.now());

  // Reset anomaly points when component mounts or visibility changes
  useEffect(() => {
    setContiguousAnomalyPoints(0);
    setPenaltyPoints(0);
    setAnomaly(false);
    setRewindVid(false);
    setPauseVid(false);
    setAnomalyDetectionStartTime(null);
  }, [setRewindVid, setPauseVid]);

  // Get our videoRef and face data from the custom hook
  const { videoRef, modelReady, faces } = useCameraProcessor(4);

  // Use refs to track initialization without causing re-renders
  const faceDetectorsKeyRef = useRef(0);
  const initializedRef = useRef(false);

   // Set grace period start time when ready
  useEffect(() => {
    if (modelReady && isVideoActive && readyToDetect && !anomalyDetectionStartTime) {
      setAnomalyDetectionStartTime(Date.now());
      console.log("⏰ Anomaly detection grace period started");
    }
  }, [modelReady, isVideoActive, readyToDetect, anomalyDetectionStartTime]);

  // Helper function to check if a specific proctoring component is enabled
  const isComponentEnabled = useCallback((componentName: string): boolean => {
    if (!settings?.settings?.proctors?.detectors) return true;
    
    const detector = settings.settings.proctors.detectors.find(
      (detector) => detector.detectorName === componentName
    );
    
    return detector?.settings?.enabled ?? false;
  }, [settings]);

  // Check which components are enabled
  const isBlurDetectionEnabled = isComponentEnabled('blurDetection');
  const isFaceCountDetectionEnabled = isComponentEnabled('faceCountDetection');
  const isHandGestureDetectionEnabled = false; //isComponentEnabled('handGestureDetection');
  const isVoiceDetectionEnabled = isComponentEnabled('voiceDetection');
  const isFaceRecognitionEnabled = isComponentEnabled('faceRecognition');
  const isRighClickDisabled = isComponentEnabled("rightClickDisabled");
  const isFocusEnabled = false; //isComponentEnabled('focus');

  // Log enabled components for debugging
  // useEffect(() => {
  //   if (settings) {
  //     console.log('🔧 [FloatingVideo] Proctoring settings loaded:', {
  //       blurDetection: isBlurDetectionEnabled,
  //       faceCountDetection: isFaceCountDetectionEnabled,
  //       handGestureDetection: isHandGestureDetectionEnabled,
  //       voiceDetection: isVoiceDetectionEnabled,
  //       faceRecognition: isFaceRecognitionEnabled,
  //       focus: isFocusEnabled
  //     });
  //   }
  // }, [settings, isBlurDetectionEnabled, isFaceCountDetectionEnabled, isHandGestureDetectionEnabled, isVoiceDetectionEnabled, isFaceRecognitionEnabled, isFocusEnabled]);

  const courseStore = useCourseStore();
  // Handle face recognition results
  const handleFaceRecognitionResult = useCallback((recognitions: any[]) => {
    // console.log('🎯 [FloatingVideo] Face recognition callback triggered with recognitions:', recognitions);
    setRecognizedFaces(recognitions);

    // Log additional info about the recognition
    const knownFaces = recognitions.filter(r => r.isMatch);
    if (knownFaces.length > 0) {
      // console.log('✅ [FloatingVideo] Known faces detected:', knownFaces.map(f => f.label).join(', '));
    } else {
      // console.log('❓ [FloatingVideo] No known faces recognized');
    }
  }, []);

  const handleFaceRecognitionMismatchChange = useCallback((hasMismatch: boolean) => {
    setHasFaceRecognitionMismatch(hasMismatch);
  }, []);

  // Handle face recognition debug info updates
  // const handleFaceRecognitionDebugUpdate = useCallback((debugInfo: FaceRecognitionDebugInfo) => {
  //   console.log('🔍 [FloatingVideo] Face recognition debug update:', debugInfo);
  //   setFaceRecognitionDebug(debugInfo);
  // }, []);

  // Store current media stream
  const [currentStream, setCurrentStream] = useState<MediaStream | null>(null);
  // const [faceDetectorsKey, setFaceDetectorsKey] = useState(0);
  const [aiComponentsKey, setAiComponentsKey] = useState(0);
  // if (flag === 0) {
  //   setFaceDetectorsKey(prev => prev + 1);
  //   flag++;
  // }

  // Use the ref value for the key
  const currentFaceDetectorsKey = faceDetectorsKeyRef.current;

   // Initialize face detectors key once on mount
  useEffect(() => {
    if (!initializedRef.current) {
      faceDetectorsKeyRef.current += 1;
      initializedRef.current = true;
    }
  }, []);

  useEffect(() => {
  if (modelReady) {
    // wait 5 seconds after models are ready before enabling anomaly detection
    const timer = setTimeout(() => {
      // console.log("[FloatingVideo] Environment ready, enabling anomaly detection");
      setReadyToDetect(true);
    }, 5000);

    return () => clearTimeout(timer);
  }
}, [modelReady]);


  // Effect to handle isPoppedOut changes - reset everything
  useEffect(() => {
    // console.log('[FloatingVideo] isPoppedOut changed to:', isPoppedOut);
    // Small delay to ensure DOM is ready, then restart video
    setTimeout(() => {
      restartVideo();
    }, 100);
  }, [isPoppedOut]);

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if (isRighClickDisabled) {
        e.preventDefault();
        console.log("Right-click is disabled on this page");
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isRighClickDisabled]); 

// Image record anomaly effect
const reportImage = useReportAnomalyImage();
const lastCalledRef = useRef<number>(0);
  useEffect(() => {
    const handleImageAnomaly = async () => {

      // To ensure that there is a minimum gap of 1 second between each request
      const now = Date.now();
      if (now - lastCalledRef.current < 1000) {
        // console.log("Throttled: waiting for 1 second gap");
        return;
      }
      lastCalledRef.current = now;

      if (anomaly && anomalyType !== "voiceDetection") {
      const video = videoRef.current;
      if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
        console.log("Video not ready for screenshot");
        return;
      }
      
      // Add validation for course data
      if (!courseStore.currentCourse?.courseId || 
          !courseStore.currentCourse?.versionId || 
          !courseStore.currentCourse?.itemId) {
        console.log("Missing course data, skipping image report");
        return;
      }

        // to convert binary to File type
        function dataURLtoFile(dataurl: string, filename: string) {
          const arr = dataurl.split(',');
          const match = arr[0].match(/:(.*?);/);
          const mime = match ? match[1] : "image/png";
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new File([u8arr], filename, { type: mime });
        }

        // to take screen shot from video
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const screenshot = canvas.toDataURL("image/png");
        const imageFile = dataURLtoFile(screenshot, "image");

        // report anomaly type checking
        let reportAnomalyType: AnomalyType;
        if (anomalyType === "blurDetection") {
          reportAnomalyType = AnomalyType.BLUR_DETECTION;
        } else if (anomalyType === "faceCountDetection") {
          // Differentiate between no face and multiple faces
          if (facesCount === 0) {
            reportAnomalyType = AnomalyType.NO_FACE;
          } else if (facesCount > 1) {
            reportAnomalyType = AnomalyType.MULTIPLE_FACES;
          } else {
            console.log("Invalid face count for anomaly reporting");
            return;
          }
        } else {
          console.log("Unknown anomaly type:", anomalyType);
          return;
        }

        // let reportAnomalyType;
        // if (anomalyType == "blurDetection") reportAnomalyType = "BLUR_DETECTION";
        // if (anomalyType == "faceCountDetection") {
        //   // Differentiate between no face and multiple faces
        //   if (facesCount === 0) {
        //     reportAnomalyType = "NO_FACE";
        //   } else if (facesCount > 1) {
        //     reportAnomalyType = "MULTIPLE_FACES";
        //   }
        // }

        // report anomaly type checking 
        // let reportAnomalyType;
        // if (anomalyType == "blurDetection") reportAnomalyType = "BLUR_DETECTION";
        // if (anomalyType == "faceCountDetection") reportAnomalyType = "MULTIPLE_FACES";
        // if (facesCount === 0) reportAnomalyType = "NO_FACE";

        // // Log for debugging
        // console.log("Image File", imageFile);
        // console.log('%c ', `font-size:1px; padding:40px 80px; background:url(${screenshot}); background-size:contain; background-repeat:no-repeat;`);
        // console.log("anomalyType", reportAnomaly, "====", "anamoly=", anomaly);
        // console.log("courseId", courseStore.currentCourse?.courseId);
        // console.log("versionId", courseStore.currentCourse?.versionId);
        // console.log("itemId", courseStore.currentCourse?.itemId);


        try {
          const response = await reportImage.mutateAsync({
            body: {
              type: reportAnomalyType as AnomalyType,
              courseId: courseStore.currentCourse?.courseId || "",
              versionId: courseStore.currentCourse?.versionId || "",
              itemId: courseStore.currentCourse?.itemId || "",
            },
            file: imageFile,
          });
          // console.log("Post response", response);

        } catch (error) {
          console.log("Error while reporting image anomaly:", error);
        }
      }
    }
    handleImageAnomaly();
  }, [anomaly, anomalyType, courseStore.currentCourse?.courseId, courseStore.currentCourse?.itemId, courseStore.currentCourse?.moduleId, courseStore.currentCourse?.sectionId, courseStore.currentCourse?.versionId]);
 
  const reportAudio = useReportAnomalyAudio();
  

  // Audio recording for speech anomaly
  useEffect(() => {
    if (isSpeaking === "Yes" && isVoiceDetectionEnabled && !mediaRecorder) {
      // Start recording
      navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        unRegisterStream("floating-video-audio-stream")
        registerStream("floating-video-audio-stream", stream);
        const recorder = new window.MediaRecorder(stream);
        setAudioChunks([]);
        setAudioStream(stream);
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            setAudioChunks(prev => [...prev, e.data]);
          }
        };
        recorder.start();
        setMediaRecorder(recorder);
      }).catch(err => {
        console.error("[FloatingVideo] Error accessing microphone for anomaly recording:", err);
      });
    }
    // If isSpeaking goes to No and we have a recorder, stop and send
    if (isSpeaking === "No" && mediaRecorder) {
      mediaRecorder.stop();
    }
    // Cleanup on unmount
    return () => {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpeaking, isVoiceDetectionEnabled]);

  // When recording stops, send audio to backend
  useEffect(() => {
    if (!mediaRecorder) return;
    const handleStop = () => {
      if (audioChunks.length > 0) {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        reportAudio.mutate({
          body: {
            type: "voiceDetection",
            courseId: courseStore.currentCourse?.courseId || "",
            versionId: courseStore.currentCourse?.versionId || "",
            itemId: courseStore.currentCourse?.itemId || ""
          },
          file: audioBlob
        });
      }
      setMediaRecorder(null);
      setAudioChunks([]);
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
        setAudioStream(null);
      }
    };
    mediaRecorder.addEventListener('stop', handleStop);
    return () => {
      mediaRecorder.removeEventListener('stop', handleStop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaRecorder, audioChunks, audioStream]);


  // Function to restart video stream
  const restartVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      // Reset all AI components by incrementing their keys
      setAiComponentsKey(prev => prev + 1);
      faceDetectorsKeyRef.current += 1;

      // Reuse the existing stream from CameraProcessor to avoid hardware conflict locks
      const stream = getStream("CameraProcessor-stream");
      if (!stream) {
        console.warn("[FloatingVideo] CameraProcessor-stream not found in registry.");
        return;
      }

      video.srcObject = stream;
      setCurrentStream(stream);

      // Setup and play video with timeout protection
      await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('Video load timeout')), 100);

        const onSuccess = () => {
          clearTimeout(timeoutId);
          setTimeout(() => {
            resolve(null);
          }, 100);
        };

        video.onloadedmetadata = () => video.play().then(onSuccess).catch(reject);
        video.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Video load error'));
        };
      });
    } catch (error) {
      console.error('[FloatingVideo] Error restarting video:', error);
    }
  }, [videoRef]);

  const navigate = useNavigate();

  const handleFaceRegistrationSuccess = useCallback(() => {
    setShowFaceRegisterModal(false);
    toast.success("Identity verified! Resuming course...");
    void restartVideo();
    setPauseVid(false);
    setReadyToDetect(true);
  }, [restartVideo, setPauseVid, setReadyToDetect]);

  const handleFaceRegistrationClose = useCallback(() => {
    setShowFaceRegisterModal(false);
    navigate({ to: '/student' });
  }, [navigate]);

  useEffect(() => {
  if (!currentStream || !readyToDetect || !isVideoActive) return;

  /**
   * Periodic Proctoring Validation
   *
   * Why:
   * - Detect camera switching after session start
   * - Detect delayed spoofing attempts
   * - Ensure continuous integrity of camera stream
   */
  cameraCheckIntervalRef.current = window.setInterval(async () => {

    const violations = await runProctoringChecks(currentStream);

    if (violations.length > 0) {
      console.error("Camera integrity violation detected:", violations[0]);

      // Stop stream immediately
      currentStream.getTracks().forEach(track => track.stop());

      // Reset stream
      setCurrentStream(null);

      // Trigger existing proctoring flow
      setPauseVid(true);
      setRewindVid(true);

      // Log anomaly (optional but recommended)
      setAnomalies([...anomalies, "cameraIntegrity"]);
    }

  }, 5000); // every 5 seconds

  return () => {
    if (cameraCheckIntervalRef.current) {
      clearInterval(cameraCheckIntervalRef.current);
      cameraCheckIntervalRef.current = null;
    }
  };

}, [currentStream, readyToDetect, isVideoActive]);


  // Update face count when faces change
  useEffect(() => {
    if (!modelReady) return;
    setFacesCount(faces.length);
  }, [faces, modelReady]);

  // Debug why TensorFlow sees 0 faces
  useEffect(() => {
  }, [facesCount, modelReady, isVideoActive]);

  // Debug face detection
  useEffect(() => {
    if (faces.length > 0) {
      // console.log('👤 [FloatingVideo] Faces detected:', {
      //   count: faces.length,
      //   details: faces.map(face => ({
      //     // TensorFlow face detection properties
      //     box: {
      //       x: face.box?.xMin,
      //       y: face.box?.yMin, 
      //       width: face.box?.width,
      //       height: face.box?.height
      //     }
      //   }))
      // });
    } else if (modelReady && isVideoActive) {
      // console.log('❌ [FloatingVideo] No faces detected (camera active)');
    }
  }, [faces, modelReady, isVideoActive]);

  // Update penalty score every second when anomalies are detected
  // useEffect(() => {

    // Don't run anomaly detection if camera isn't ready or no video stream
    // if (!readyToDetect || !videoRef.current || !videoRef.current.srcObject) {
    //   return;
    // }

    // const interval = setInterval(() => {
    //   if (!readyToDetect || !videoRef.current || !videoRef.current.srcObject) {
    //     return;
    //   }
  
    // Update penalty score every second when anomalies are detected
    useEffect(() => {
    if (!readyToDetect || !videoRef.current || !isVideoActive) {
      if (contiguousAnomalyPoints > 0) {
        setContiguousAnomalyPoints(0); // Reset when video is not active
      }
      return;
    }

    // Grace period check
    const currentTime = Date.now();
    const isInGracePeriod = anomalyDetectionStartTime && 
                            (currentTime - anomalyDetectionStartTime < gracePeriod);
    
    if (isInGracePeriod) {
      const remainingGrace = gracePeriod - (currentTime - anomalyDetectionStartTime);
      console.log(`⏳ In grace period: ${Math.ceil(remainingGrace / 1000)}s remaining`);
      return; // Skip anomaly detection during grace period
    }

    const interval = setInterval(() => {
    if (!readyToDetect || !videoRef.current || !isVideoActive) {
      return;
    }
      //  if (!readyToDetect){ return; // Skip anomaly detection until warmed up
      // }
      const activeAnomalies: string[] = [];
      let newPenaltyPoints = 0;
      let newPenaltyType = "";

      // Condition 1: If speaking is detected (only if voice detection is enabled)
      if (isSpeaking === "Yes" && isVoiceDetectionEnabled) {
        activeAnomalies.push("voiceDetection");
        newPenaltyType = "Speaking";
        newPenaltyPoints += 1;
      }

      // Condition 2: Handle different face count scenarios separately (only if face count detection is enabled)
      let hasMultipleFaces = false;
      if (isFaceCountDetectionEnabled && modelReady) {
        if (facesCount === 0) {
          // No faces detected - this might be normal during initialization
          // Only penalize if we're sure the camera is active and should see a face
          if (isVideoActive && readyToDetect) {
            // Tag with "noFace" so the alert can say "Please stay in frame"
            activeAnomalies.push("faceCountDetection", "noFace");
            newPenaltyType = "No Face Detected";
            newPenaltyPoints += 1;
            // Surface the live camera frame so the learner can reposition
            setIsCollapsed(false);
          }
        } else if (facesCount > 1) {
          // Multiple faces detected - this is an anomaly
          // Tag with "multipleFaces" for alert messaging; pause/rewind is driven
          // by the hasMultipleFaces flag below (immediatePause + immediateRewind).
          hasMultipleFaces = true;
          activeAnomalies.push("faceCountDetection", "multipleFaces");
          newPenaltyType = "Multiple Faces";
          newPenaltyPoints += 2; // More severe penalty for multiple faces
        }
        // facesCount === 1 is normal, no penalty
      }

      // Condition 3: If the screen is blurred (only if blur detection is enabled)
      if (isBlur === "Yes" && isBlurDetectionEnabled) {
        activeAnomalies.push("blurDetection");
        newPenaltyType = "Blur";
        newPenaltyPoints += 1;
      }

      // Condition 4: If not focused (only if focus tracking is enabled)
      if (!isFocused && isFocusEnabled) {
        activeAnomalies.push("focus");
        newPenaltyType = "Focus";
        newPenaltyPoints += 1;
      }

      // Condition 5: Registered face does not match current camera frame
      let hasFaceMismatch = false;
      if (hasFaceRecognitionMismatch && isFaceRecognitionEnabled) {
        hasFaceMismatch = true;
        activeAnomalies.push("faceRecognition");
        newPenaltyType = "Face Recognition";
        newPenaltyPoints += 2;
      }

      setAnomalies(activeAnomalies);

      // Determine if we need to pause or rewind immediately
      let immediatePause = false;
      let immediateRewind = false;

      if (hasFaceMismatch) {
        immediatePause = true;
      }

      if (hasMultipleFaces) {
        immediatePause = true;
        immediateRewind = true;
      }

      // If there are any new penalty points, increment the cumulative score
      if (newPenaltyPoints > 0) {
        setAnomaly(true);
        setPenaltyPoints((prevPoints) => prevPoints + newPenaltyPoints);
        setPenaltyType(newPenaltyType);
        setAnomalyType(
          newPenaltyType === "Focus" ? "focus" :
          newPenaltyType === "Blur" ? "blurDetection" :
          newPenaltyType === "No Face Detected" || newPenaltyType === "Multiple Faces" ? "faceCountDetection" :
          newPenaltyType === "Speaking" ? "voiceDetection" :
          newPenaltyType === "Pre-emptive Thumbs-Up" || newPenaltyType === "Failed Thumbs-Up Challenge" ? "handGestureDetection" :
          "faceRecognition"
        );
        
        // Update contiguous points and handle pauses
        setContiguousAnomalyPoints((prev) => {
          const newContiguous = prev + newPenaltyPoints;
          
          if (newContiguous >= 20) {
            console.log(`[FloatingVideo] Rewind triggered: 20 continuous anomaly points reached`);
            setRewindVid(true);
            setPauseVid(true);
            return 0;
          }

          if (immediatePause) {
            setPauseVid(true);
          }
          if (immediateRewind) {
            setRewindVid(true);
          }

          // If no immediate pause conditions are active and we are under the 20 points threshold,
          // clear pause/rewind states if they were set by a previous face recognition mismatch.
          if (!immediatePause && !immediateRewind && newContiguous < 20) {
            setPauseVid(false);
            setRewindVid(false);
          }

          return newContiguous;
        });
      }
      else {
        setAnomaly(false);
        setAnomalies([]);
        // When anomalies are cleared, restore previous video state
        if (rewindVid || pauseVid) {
          setRewindVid(false);
          setPauseVid(false);  // Resume video when anomalies are cleared
        }
        // Reset contiguous anomaly points when no anomalies are detected
        if (contiguousAnomalyPoints > 0) {
          setContiguousAnomalyPoints(0);
        }
      }
    }, 100); // Update every second

    return () => clearInterval(interval);
  }, [readyToDetect,
    isSpeaking, 
    facesCount, 
    isBlur, 
    isFocused, 
    // reportAnomaly,
    courseStore.currentCourse,
    isVoiceDetectionEnabled,
    isFaceCountDetectionEnabled,
    isBlurDetectionEnabled,
    isFocusEnabled,
    isFaceRecognitionEnabled,
    hasFaceRecognitionMismatch,
    // data,
    // error,
    rewindVid,
    setRewindVid,
    pauseVid,
    setPauseVid,
    contiguousAnomalyPoints,
    modelReady,
    anomalyDetectionStartTime,
    gracePeriod
  ]);


  const min = 2 * 60 * 1000;
  const max = 5 * 60 * 1000;
  // Random thumbs-up challenge system - only run if gesture detection is enabled
  useEffect(() => {
    if (!isHandGestureDetectionEnabled) return;
    
    const checkForChallenge = () => {
      const now = Date.now();
      const timeSinceLastChallenge = now - lastChallengeTime;

      // Only trigger if no active challenge and enough time has passed (2-5 minutes randomly)
      if (!isThumbsUpChallenge && timeSinceLastChallenge > 30000) { // Minimum 30 seconds for testing
        const randomInterval = Math.random() * (max-min) + min; // 2-5 minutes

        if (timeSinceLastChallenge > randomInterval) {
          // Check if user is already showing thumbs-up when challenge starts
          const gestureText = gesture.toLowerCase();
          const isAlreadyThumbsUp = gestureText.includes("thumb_up") || gestureText === "thumb_up";

          if (isAlreadyThumbsUp) {
            // Add penalty for pre-emptive thumbs-up
            setPenaltyPoints(prevPoints => prevPoints + 1);
            setPenaltyType("Pre-emptive Thumbs-Up");
            setLastChallengeTime(now); // Update time to prevent immediate next challenge
          } else {
            // Show alert without blocking execution
            setDoGesture(true);
            setIsThumbsUpChallenge(true);
            setThumbsUpCountdown(10);
            setLastChallengeTime(now);
          }
        }
      }
    };

    const interval = setInterval(checkForChallenge, 1000);
    return () => clearInterval(interval);
  }, [isThumbsUpChallenge, lastChallengeTime, gesture, isHandGestureDetectionEnabled, setDoGesture]);

  // Thumbs-up countdown timer - only run if gesture detection is enabled
  useEffect(() => {
    if (!isHandGestureDetectionEnabled || !isThumbsUpChallenge || thumbsUpCountdown <= 0) return;

    const timer = setTimeout(() => {
      setThumbsUpCountdown(prev => {
        if (prev <= 1) {
          // Time's up - add penalty and end challenge
          setPenaltyPoints(prevPoints => prevPoints + 2);
          setPenaltyType("Failed Thumbs-Up Challenge");
          setDoGesture(false);
          setIsThumbsUpChallenge(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [isThumbsUpChallenge, thumbsUpCountdown, isHandGestureDetectionEnabled, setDoGesture]);

  // Check for thumbs-up gesture during challenge - only run if gesture detection is enabled
  useEffect(() => {
    if (!isHandGestureDetectionEnabled || !isThumbsUpChallenge || !gesture) return;
    
    const gestureText = gesture.toLowerCase();
    console.log(`[Challenge] Current gesture detected: "${gesture}" (normalized: "${gestureText}")`);
    
    // Check for various thumbs-up patterns that MediaPipe might return
    const isThumbsUp = gestureText.includes("thumb_up") || gestureText === "thumb_up";
    
    if (isThumbsUp) {
      setDoGesture(false);
      // Success - end challenge without penalty
      setIsThumbsUpChallenge(false);
      setThumbsUpCountdown(0);
    }
  }, [gesture, isThumbsUpChallenge, isHandGestureDetectionEnabled, setDoGesture]);

  // Check if any anomalies are detected - only consider enabled detectors
  const isAnomaliesDetected = (isSpeaking === "Yes" && isVoiceDetectionEnabled) || 
                              (facesCount !== 1 && isFaceCountDetectionEnabled) || 
                              (isBlur === "Yes" && isBlurDetectionEnabled) || 
                              (!isFocused && isFocusEnabled) ||
                              (hasFaceRecognitionMismatch && isFaceRecognitionEnabled) ||
                              (isThumbsUpChallenge && isHandGestureDetectionEnabled);

  useEffect(() => {
    console.log('[FaceRecognitionDebug] floating-video state', {
      hasFaceRecognitionMismatch,
      isFaceRecognitionEnabled,
      isAnomaliesDetected,
      recognizedFaces,
      readyToDetect,
      modelReady,
      isVideoActive,
    });
  }, [
    hasFaceRecognitionMismatch,
    isFaceRecognitionEnabled,
    isAnomaliesDetected,
    recognizedFaces,
    readyToDetect,
    modelReady,
    isVideoActive,
  ]);


  // Smart overlay positioning based on face detection
  useEffect(() => {
    if (!faces.length || !faces[0]) return;

    const face = faces[0];
    if (!face.box) return;

    const videoElement = videoRef.current;
    if (!videoElement) return;

    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return;

    // Calculate face position relative to video (0-1 scale)
    const faceX = (face.box.xMin + face.box.width / 2) / videoWidth;
    const faceY = (face.box.yMin + face.box.height / 2) / videoHeight;

    // Position overlay opposite to face position
    if (faceX >= 0.5 && faceY < 0.5) {
      setOverlayPosition('bottom-left');
    } else if (faceX < 0.5 && faceY < 0.5) {
      setOverlayPosition('bottom-right');
    } else if (faceX >= 0.5 && faceY >= 0.5) {
      setOverlayPosition('top-left');
    } else {
      setOverlayPosition('top-right');
    }
  }, [faces, videoRef]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Check if clicking on resize handle first
    const target = e.target as HTMLElement;
    if (target.classList.contains('resize-handle')) {
      e.preventDefault();
      setIsResizing(true);
      return;
    }

    // Ignore clicks on buttons
    if (target.closest('button')) {
      return;
    }

    // Start dragging
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      requestAnimationFrame(() => {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      });
    } else if (isResizing) {
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        requestAnimationFrame(() => {
          const newWidth = Math.max(200, e.clientX - rect.left);
          // Maintain original aspect ratio
          const newHeight = Math.max(150, newWidth / ORIGINAL_ASPECT_RATIO);
          setSize({ width: newWidth, height: newHeight });
        });
      }
    }
  }, [isDragging, isResizing, dragOffset, ORIGINAL_ASPECT_RATIO]);

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection during drag/resize
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Restore text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [isDragging, isResizing, handleMouseMove]);

  // Comprehensive face detection debug
  useEffect(() => {
    // console.log('🔍 [FloatingVideo] FACE DETECTION DEBUG:', {
    //   // TensorFlow detection
    //   tensorFlowFaces: faces.length,
    //   tensorFlowFacesCount: facesCount,
      
    //   // Face-api.js recognition  
    //   recognizedFacesCount: recognizedFaces.length,
      
    //   // System states
    //   modelReady,
    //   isVideoActive,
    //   readyToDetect,
      
    //   // Video status
    //   videoReady: videoRef.current?.readyState,
    //   videoWidth: videoRef.current?.videoWidth,
    //   videoHeight: videoRef.current?.videoHeight,
    //   hasStream: !!videoRef.current?.srcObject
    // });

    // Log actual face data if available
    if (faces.length > 0) {
      console.log('👤 TensorFlow Face Details:', faces);
    }
    if (recognizedFaces.length > 0) {
      console.log('🎭 Face-api Recognition Details:', recognizedFaces);
    }
  }, [faces, facesCount, recognizedFaces, modelReady, isVideoActive, readyToDetect]);

  // Monitor video stream state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const checkStreamState = () => {
      const hasStream = !!video.srcObject;
      const isPlaying = !video.paused && !video.ended && video.readyState > 2;
      const isActive = hasStream && isPlaying;
      setIsVideoActive(isActive);
      
      // Debug log
      if (!isActive) {
        console.log('[FloatingVideo] Video not active:', {
          hasStream,
          isPlaying,
          paused: video.paused,
          ended: video.ended,
          readyState: video.readyState
        });
      }
    };

  // Check initially
  checkStreamState();

  // Set up interval to check stream state
  const interval = setInterval(checkStreamState, 2000);

  // Listen for video events
  video.addEventListener('play', checkStreamState);
  video.addEventListener('pause', checkStreamState);
  video.addEventListener('ended', checkStreamState);
  video.addEventListener('loadedmetadata', checkStreamState);

  return () => {
    clearInterval(interval);
    video.removeEventListener('play', checkStreamState);
    video.removeEventListener('pause', checkStreamState);
    video.removeEventListener('ended', checkStreamState);
    video.removeEventListener('loadedmetadata', checkStreamState);
  };
}, [videoRef]);

  // Enhanced video lifecycle management
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => { });
    };

    const handlePause = () => {
      // If not intentionally paused (muted, hidden, etc.), try to play
      if (video.paused && !video.ended) {
        video.play().catch(() => { });
      }
    };

    const handleError = () => {
      console.log('[FloatingVideo] Video error, restarting stream...');
      restartVideo();
    };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('error', handleError);
    };
  }, [videoRef, restartVideo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [currentStream]);

  if (!isVisible) return null;

  const videoHeight = size.height - 30; // Always maintain video height for AI detection
  const containerHeight = isCollapsed ? 34 : size.height; // Just header height when collapsed

  const getOverlayClasses = () => {
    const baseClasses = "absolute text-white p-2 text-xs pointer-events-none max-w-[50%] backdrop-blur-sm transition-all duration-500";

    switch (overlayPosition) {
      case 'top-left':
        return `${baseClasses} top-0 left-0`;
      case 'top-right':
        return `${baseClasses} top-0 right-0`;
      case 'bottom-left':
        return `${baseClasses} bottom-0 left-0`;
      case 'bottom-right':
        return `${baseClasses} bottom-0 right-0`;
      default:
        return `${baseClasses} top-0 right-0`;
    }
  };

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
  };

  // Calculate grace period remaining
  const currentTime = Date.now();
  const isInGracePeriod = anomalyDetectionStartTime && 
                          (currentTime - anomalyDetectionStartTime < gracePeriod);
  const remainingGrace = isInGracePeriod ? gracePeriod - (currentTime - anomalyDetectionStartTime) : 0;

  const floatingVideoContent = (
    <div
      ref={containerRef}
      className={`z-[999999] bg-black rounded-lg shadow-lg  overflow-hidden select-none transition-all duration-300 ${isPoppedOut
        ? 'fixed'
        : 'relative max-w-[200px]'
        }`}
      style={isPoppedOut ? {
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${containerHeight}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      } : isCollapsed ? {
        height: '34px',
        width: '100%',
      } : {
        height: `${size.height}px`
      }}
      onMouseDown={isPoppedOut ? handleMouseDown : undefined}
    >

      {/* Grace period status display */}
      {isInGracePeriod && (
         <div className="absolute top-1 left-1 bg-blue-600 text-white px-2 py-1 rounded text-xs z-50">
          ⏳ Calibrating: {Math.ceil(remainingGrace / 1000)}s
        </div>
      )}

      {/* Header - Anomaly state */}
      {isAnomaliesDetected && (
        <div className={`bg-red-600 text-white px-3 py-1 flex justify-between items-center text-sm min-h-[34px]`}>
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <span className="font-medium truncate">
              {isThumbsUpChallenge
                ? `👍 Show Thumbs Up: ${thumbsUpCountdown}s`
                : `⚠️ ${isCollapsed ? `${penaltyType || 'Anomalies'} (${penaltyPoints})` : 'Detected Anomalies!'}`
              }
            </span>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapse}
              className="h-6 w-6 p-0 text-white hover:bg-green-700 hover:text-white flex-shrink-0"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? <ChevronUp className="h-3 w-3" />: <ChevronDown className="h-3 w-3" /> }
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsPoppedOut(!isPoppedOut);
              }}
              className="h-6 w-6 p-0 text-white hover:bg-current hover:text-white flex-shrink-0"
            >
              {isPoppedOut ? <SquareArrowOutDownLeft className="h-3 w-3" /> : <PictureInPicture2 className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      )}

      {/* Header - Normal state */}
      {!isAnomaliesDetected && (
        <div className="bg-green-600 text-white px-3 py-1 flex justify-between items-center text-sm">
          <div className="flex items-center space-x-2 flex-1">
            <span className="font-medium">☑️ {isCollapsed ? `All Clear (${penaltyPoints})` : 'All Clear'}</span>
            {/* Face Recognition Status */}
            {!isCollapsed && recognizedFaces.length > 0 && (
              <span className="text-xs opacity-90">
                | Recognized: {recognizedFaces.filter(f => f.isMatch).map(f => f.label).join(', ') || 'Unknown'}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapse}
              className="h-6 w-6 p-0 text-white hover:bg-green-700 hover:text-white"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? <ChevronUp className="h-3 w-3" /> :<ChevronDown className="h-3 w-3" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsPoppedOut(!isPoppedOut);
              }}
              className="h-6 w-6 p-0 text-white hover:bg-current hover:text-white"
            >
              {isPoppedOut ? <SquareArrowOutDownLeft className="h-3 w-3" /> : <PictureInPicture2 className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      )}

      {/* Video element - Always present and functional for AI detection */}
      <div
        className="absolute top-8 left-0"
        style={{
          width: isCollapsed ? '1px' : '100%',
          height: isCollapsed ? '1px' : `${videoHeight}px`,
          overflow: 'hidden'
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover bg-gray-800"
          style={{
            transform: 'scaleX(-1)',
            // Always keep video visible to AI components
            opacity: 1,
          }}
        />
{!modelReady && (
  <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 text-white text-sm font-medium">
    Preparing environment...
  </div>
)}
{modelReady && isFaceCountDetectionEnabled && facesCount === 0 && !isCollapsed && (
  <div className="absolute inset-x-0 bottom-0 z-40 bg-black bg-opacity-70 text-white text-sm font-medium text-center py-2 px-2">
    Please stay in frame
  </div>
)}
        {/* Enhanced Face Recognition Debug Overlay */}
        {/* {!isCollapsed && (
          <div className="absolute top-0 left-0 z-20 bg-black bg-opacity-75 text-white p-3 text-xs font-mono border-r border-b border-gray-600">
          <div className="space-y-1">
            {/* Backend Status */}
        {/* <div className="flex items-center gap-2">
            <span className="text-gray-300">Backend:</span>
            <span className={`font-semibold ${
              faceRecognitionDebug.backendStatus === 'success' ? 'text-green-400' : 
              faceRecognitionDebug.backendStatus === 'error' ? 'text-red-400' : 'text-yellow-400'
            }`}>
              {faceRecognitionDebug.backendStatus.toUpperCase()}
            </span>
            </div>
            
            {/* Known Faces Info */}
        {/* <div className="text-blue-300">
            Known People: {faceRecognitionDebug.knownFacesCount}
            </div>
            
            {/* Photo Faces Info */}
        {/* <div className="text-purple-300">
            Photo Faces: {faceRecognitionDebug.detectedPhotoFaces}
            </div>
            
            {/* Current Frame Info */}
        {/* <div className="text-cyan-300">
            Frame Faces: {faceRecognitionDebug.currentFrameFaces}
            </div>
            
            {/* Recognition Results */}
        {/* <div className="text-green-300">
            Recognized: {faceRecognitionDebug.recognizedFaces}
            </div>
            
            {/* Processing Time */}
        {/* {faceRecognitionDebug.processingTime && (
            <div className="text-orange-300">
              Process: {faceRecognitionDebug.processingTime.toFixed(1)}ms
            </div>
            )}
            
            {/* Known Face Labels */}
        {/* {faceRecognitionDebug.knownFaceLabels.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div className="text-gray-300 mb-1">Known:</div>
              <div className="text-yellow-300 text-xs">
              {faceRecognitionDebug.knownFaceLabels.join(', ')}
              </div>
            </div>
            )}
            
            {/* Current Recognition Results */}
        {/* {recognizedFaces.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div className="text-gray-300 mb-1">Current:</div>
              {recognizedFaces.map((face, idx) => (
              <div key={idx} className={`text-xs ${face.isMatch ? 'text-green-300' : 'text-red-300'}`}>
                {face.label} ({face.distance.toFixed(3)})
              </div>
              ))}
            </div>
            )}
            
            {/* Error Message */}
        {/* {faceRecognitionDebug.errorMessage && (
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div className="text-red-400 text-xs">Error: {faceRecognitionDebug.errorMessage}</div>
            </div>
            )}
            
            {/* Last Update Time */}
        {/* <div className="mt-2 pt-2 border-t border-gray-600 text-gray-400 text-xs">
            Updated: {new Date(faceRecognitionDebug.lastUpdateTime).toLocaleTimeString()}
            </div>
          </div>
          </div>
        )} */}
        {/* )} */}
        {isFaceRecognitionEnabled && !isCollapsed && recognizedFaces.length > 0 && (
          <FaceRecognitionOverlay
            recognitions={recognizedFaces}
            videoRef={videoRef}
            className="z-10"
          />
        )}

        {/* Canvas for face detection - Always present */}
        <canvas
          ref={canvasRef}
          className="hidden"
        />

        {/* Face Recognition Overlay
        {modelReady && (
          <>
            {console.log('🔍 [FloatingVideo] Rendering FaceRecognitionIntegrated with:', {
              facesCount: faces.length,
              hasVideo: !!videoRef.current,
              modelReady
            })}
            <FaceRecognitionIntegrated
              faces={faces}
              videoElement={videoRef.current}
              modelReady={modelReady}
              onRecognitionResult={handleFaceRecognitionResult}
            />
          </>
        )} */}

        {/* Overlay and UI elements - Only show when not collapsed */}
        {!isCollapsed && (
          <>
            {/* Overlay - Only show when anomalies are detected */}
            {isAnomaliesDetected && (
              <div className={getOverlayClasses()} style={{ background: "rgba(0, 0, 0, 0.7)", pointerEvents: 'none' }}>
                <div className="space-y-1">
                  <div className="font-semibold text-yellow-300 mb-1 drop-shadow-lg">
                    {isThumbsUpChallenge ? "Security Challenge" : "Anomaly Details"}
                  </div>

                  <div className="space-y-0.5 text-[9px] drop-shadow-md">
                    {isThumbsUpChallenge && (
                      <div className="text-yellow-300 font-medium">
                        👍 Show Thumbs Up: <span className="text-red-400">
                          {thumbsUpCountdown}s
                        </span>
                      </div>
                    )}

                    {!isThumbsUpChallenge && (
                      <>
                        {isBlur === "Yes" && (
                          <div>Blur: <span className="text-red-400">
                            {isBlur}
                          </span></div>
                        )}

                        {isSpeaking === "Yes" && (
                          <div>Speaking: <span className="text-red-400">
                            {isSpeaking}
                          </span></div>
                        )}

                        {!isFocused && (
                          <div>Focus: <span className="text-red-400">
                            Not Focused
                          </span></div>
                        )}

                        {isFaceCountDetectionEnabled && facesCount !== 1 && (
                          <div>Faces: <span className="text-red-400">
                            {facesCount} {facesCount === 0 ? "(None detected)" : facesCount > 1 ? "(Multiple detected)" : ""}
                          </span></div>
                        )}

                        {/* Face Recognition Results */}
                        {isFaceRecognitionEnabled && recognizedFaces.length > 0 && (
                          <div>Recognized:
                            {recognizedFaces.map((face, index) => (
                              <span key={index} className={face.isMatch ? "text-green-400" : "text-yellow-400"}>
                                {index > 0 ? ", " : " "}
                                {face.isMatch ? face.label : "Unknown"}
                                {face.isMatch && ` (${Math.round((1 - face.distance) * 100)}%)`}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    <div className="border-t border-gray-500 pt-1 mt-2">
                      <div>Penalty Score: <span className="text-red-400 font-medium">
                        {penaltyPoints}
                      </span></div>
                      {penaltyType && (
                        <div>Last Issue: <span className="text-orange-300">
                          {penaltyType}
                        </span></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Score display in corner when no anomalies */}
            {!isAnomaliesDetected && penaltyPoints > 0 && (
              <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs pointer-events-none">
                Score: {penaltyPoints}
              </div>
            )}

          </>
        )}
      </div>

      {/* AI Components - Only render if enabled in proctoring settings */}
      <div className="hidden">
        {isBlurDetectionEnabled && (
          <BlurDetection 
            key={`blur-${aiComponentsKey}`}
            videoRef={videoRef} 
            setIsBlur={setIsBlur}
          />
        )}
        {isVoiceDetectionEnabled && (
          <SpeechDetector 
            key={`speech-${aiComponentsKey}`}
            setIsSpeaking={setIsSpeaking}
          />
        )}
        {isHandGestureDetectionEnabled && (
          <GestureDetector 
            key={`gesture-${aiComponentsKey}`}
            videoRef={videoRef} 
            setGesture={setGesture}
            trigger={true}
          />
        )}
        {(isFaceCountDetectionEnabled || isFaceRecognitionEnabled || isFocusEnabled) && (
          <FaceDetectors 
            key={`face-${faceDetectorsKeyRef.current}`}
            faces={faces} 
            setIsFocused={()=>{}}
            videoRef={videoRef}
            onRecognitionResult={handleFaceRecognitionResult}
            onMismatchChange={handleFaceRecognitionMismatchChange}
            onMissingEmbedding={() => {
              setPauseVid(true);
              setReadyToDetect(false);
              setShowFaceRegisterModal(true);
            }}
            settings={{
              isFaceCountDetectionEnabled,
              isFaceRecognitionEnabled, 
              isFocusEnabled
            }}
          />
  
          // <FaceDetectors 
          //   key={`face-${faceDetectorsKey}`}
          //   faces={faces} 
          //   setIsFocused={()=>{}} // CHANGE THIS LATER.
          //   videoRef={videoRef}
          //   onRecognitionResult={handleFaceRecognitionResult}
          //   // onDebugInfoUpdate={handleFaceRecognitionDebugUpdate}
          //   settings={isFaceCountDetectionEnabled, isFaceRecognitionEnabled, isFocusEnabled}
          // />
        )}
      </div>

      {/* Resize Handle - Only show when not collapsed and popped out */}
      {!isCollapsed && isPoppedOut && (
        <div
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-gray-600 opacity-60 hover:opacity-100"
          style={{
            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
            pointerEvents: 'auto'
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsResizing(true);
          }}
        />
      )}
    </div>
  );

  const modalPortal = (showFaceRegisterModal && typeof window !== 'undefined') ? ReactDOM.createPortal(
    <FaceRegistrationModal
      isOpen={showFaceRegisterModal}
      onSuccess={handleFaceRegistrationSuccess}
      onClose={handleFaceRegistrationClose}
    />,
    document.body
  ) : null;

  // Use portal to render floating video at the end of body if popped out
  if (isPoppedOut && typeof window !== 'undefined') {
    return (
      <>
        {ReactDOM.createPortal(floatingVideoContent, document.body)}
        {modalPortal}
      </>
    );
  }
  // Otherwise render in place (sidebar)
  return (
    <>
      {floatingVideoContent}
      {modalPortal}
    </>
  );
};

export default FloatingVideo;


export function FloatingVideoPlaceholder() {
  const height:number=196;
  return (
    <div
      className="w-full bg-black rounded-lg shadow-lg overflow-hidden"
      style={{ height }}
    >
      <div className="bg-red-600 text-white px-3 py-1 text-sm flex items-center">
        <span className="font-medium truncate">Accept the declaration</span>
      </div>
      <div className="flex flex-col items-center justify-center" style={{height:height-34}}>
          <div className="w-14 h-14 rounded-full bg-gray-500 animate-pulse" />
        </div>
    </div>
  );
}
